import { Form } from 'antd';
import { useState } from 'react';
import { format, parse } from 'date-fns';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { putService, getVerifyTechnicianAvailability } from 'queries';
import { isTimeExpired, parseErrors, Toast } from 'helpers';
import { JsonDataItem, ReschedulingSteps, SlotAvailability, TimeSlot } from 'types';

export const useRescheduleSlot = (
  job: JsonDataItem | null,
  slot: TimeSlot | null,
  technician: JsonDataItem | null,
  reasonId: number | null,
  isAvailabilityOverrideEnabled: boolean,
  serviceSelection: JsonDataItem | null,
) => {
  const [slotForm] = Form.useForm();
  const [isTechConfirmed, setIsTechConfirmed] = useState<boolean>(false);
  const navigate = useNavigate();
  const isTimeSlotExpiring =
    !!slot &&
    isTimeExpired(
      `${format(parse(slot.day, 'dd-MM-yyyy', new Date()), 'yyyy-MM-dd')} ${slot.time}`,
      job?.customer?.timezone,
      2,
    );

  const {
    mutate,
    data: rescheduledJobData,
    isLoading: isAppointmentUpdating,
  } = useMutation(putService, {
    onSuccess: () => {
      Toast.show({
        description: 'Rescheduled successfully!',
        type: Toast.Type.Success,
      });
      navigate(`/job/reschedule/${job?.ID}/${ReschedulingSteps.SendNotification}`);
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: verifyAndReschedule, isLoading: isVerifying } = useMutation(getVerifyTechnicianAvailability, {
    onSuccess: () => {
      rescheduleJob();
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const rescheduleJob = () => {
    const isConfirmed =
      isTimeSlotExpiring || slot?.availability === SlotAvailability.Confirmation ? Number(isTechConfirmed) : undefined;
    mutate({
      technicianIds: [technician?.ID],
      appointmentStart: slot?.day && `${format(parse(slot.day, 'dd-MM-yyyy', new Date()), 'yyyy-MM-dd')} ${slot?.time}`,
      reasonId: reasonId as number,
      jobId: job?.ID,
      isTechnicianConfirmed: isConfirmed as 0 | 1 | undefined, // ...
      isAvailabilityOverrideEnabled,
    });
  };

  const handleRescheduleJob = () => {
    isAvailabilityOverrideEnabled
      ? verifyAndReschedule({
          technicianId: technician?.ID,
          startDate: slot?.day
            ? `${format(parse(slot.day, 'dd-MM-yyyy', new Date()), 'yyyy-MM-dd')} ${slot?.time}`
            : '',
          serviceSelectionId: serviceSelection?.ID,
          serviceType: job?.service_type_ID,
        })
      : rescheduleJob();
  };

  return {
    slotForm,
    rescheduledJobData,
    isTechConfirmed,
    setIsTechConfirmed,
    rescheduleJob: handleRescheduleJob,
    isAppointmentUpdating: isAppointmentUpdating || isVerifying,
  };
};
